import { PDPProduct } from "@/app/product/[product]/page";
import { ProductCard } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { CartItemMaybeHooked } from "@/lib/hooks/useSelection/cart/useItems";
import { klaviyoTrack } from "@/lib/klaviyo/track";
import { CentraVoucher } from "@frend-digital/centra-legacy/schemas/selection/utilitySchemas";

declare global {
  interface Window {
    dataLayer?: Object[];
    ADT: any;
  }
}

/**
 * Hardcoded rather than derived from window.location.origin: these URLs end up
 * in Klaviyo emails, so a preview deployment must never send links pointing at
 * itself.
 */
export const SITE_URL = "https://camillapihl.com";

const productUrl = (uri?: string) =>
  uri ? `${SITE_URL}/product/${uri}` : SITE_URL;

export const pageview = (url: string) => {
  window?.dataLayer?.push({
    config: `${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}`,
    page_path: url,
  });
};

interface GTagEvent {
  action: string;
  category: string;
  label: string;
  value: number;
}

export const event = ({ action, category, label, value }: GTagEvent) => {
  window?.dataLayer?.push({
    event: action,
    event_category: category,
    event_label: label,
    revenue: value,
  });
};

export const generateGTagItem = (
  item: PDPProduct,
  price?: number,
  additionalFields?: Record<string, string | number>,
) => {
  const item_id = item?.sku || "";
  const item_name = item?.name || "";
  const item_url = productUrl(item?.uri);
  const item_category = item?.breadcrumbs?.[0]?.name || "";
  const item_variant = item?.variantName || "";
  const item_price = price || NaN;
  const item_quantity = 1;
  const item_image = item?.media?.[0].href;

  return {
    item_id,
    item_name,
    item_category,
    item_variant,
    price: item_price,
    quantity: item_quantity,
    item_url,
    item_image,
    ...additionalFields,
  };
};

export const convertPCToGtagItem = (
  item: ProductCard,
  price?: number,
  quantity: number = 1,
  additionalFields?: Record<string, string | number>,
) => {
  const item_id = item.sku || "";
  const item_name = item?.core?.name || "";
  const item_category = ""; // TODO - Find category from depict
  const item_variant = item?.core?.variantName || "";
  const item_price = price || NaN;
  const item_quantity = quantity;

  return {
    item_id,
    item_name,
    item_category,
    item_variant,
    price: item_price,
    quantity: item_quantity,
    facebook_feed_g_id:
      item?.productSku && item.sku
        ? item.sku.replace(item.productSku, `${item.productSku}-`)
        : "",
    ...additionalFields,
  };
};

export const convertCartToGTagItem = (item: CartItemMaybeHooked) => {
  const item_id = item?.sku || "";
  const item_name = item?.name || "";
  const item_category = item?.category || "";
  const item_variant = item?.variant || "";
  const item_price = item.priceAsNumber || NaN;
  const item_quantity = item.quantity;
  const size_name = item.size;

  return {
    item_id,
    item_name,
    item_category,
    item_variant,
    price: item_price,
    quantity: item_quantity,
    size_name,
    facebook_feed_g_id: `${(
      item as {
        productSku?: string;
      }
    )?.productSku?.slice(0, 5)}-${(
      item as {
        productSku?: string;
      }
    )?.productSku?.slice(-4)}`,
  };
};

export interface GTagItem {
  item_id: string; // SKU
  item_name: string; // Product Name
  discount?: number;
  item_category: string; // Top Category /women
  item_category2?: string; // Sub Category /women/!dresses!
  item_category3?: string;
  item_variant: string; // Variant name
  price: number; // Price
  quantity?: number; // How many
  item_url?: string;
  item_image?: string;
  size_name?: string;
}
interface EventCartProps {
  currency: string;
  value?: number;
  items: GTagItem[] | [];
  $email?: string;
}

/**
 * The cart as it looked before the item was added, plus the itemId being added,
 * so the event can carry a full basket snapshot rather than just the new line.
 */
export interface AddToCartCartContext {
  items: CartItemMaybeHooked[];
  total: number;
  addedItemId?: string;
}

interface EventAddToCartProps extends EventCartProps {
  cart?: AddToCartCartContext;
  $email?: string;
}

export const toKlaviyoItem = (item: GTagItem) => {
  const quantity = item.quantity ?? 1;
  const price = Number.isFinite(item.price) ? item.price : 0;

  return {
    ProductID: item.item_id,
    SKU: item.item_id,
    ProductName: item.item_name,
    Quantity: quantity,
    ItemPrice: price,
    RowTotal: price * quantity,
    ProductCategories: item.item_category ? [item.item_category] : [],
    Size: item.size_name,
  };
};

/**
 * Merges the item being added into the pre-add cart snapshot. If the same size
 * is already in the cart we bump its quantity instead of listing it twice.
 */
const buildKlaviyoCartItems = (
  addedItem: GTagItem,
  cart?: AddToCartCartContext,
) => {
  const cartLines = cart?.items ?? [];
  const items = cartLines.map(line =>
    toKlaviyoItem(convertCartToGTagItem(line)),
  );
  const added = toKlaviyoItem(addedItem);

  const alreadyInCart = cart?.addedItemId
    ? cartLines.findIndex(item => item.itemId === cart.addedItemId)
    : -1;

  if (alreadyInCart > -1 && items[alreadyInCart]) {
    const line = items[alreadyInCart];
    const quantity = line.Quantity + added.Quantity;
    items[alreadyInCart] = {
      ...line,
      Quantity: quantity,
      RowTotal: line.ItemPrice * quantity,
    };
    return items;
  }

  return [...items, added];
};

export const eventAddToCart = ({
  currency,
  value,
  items,
  cart,
  $email,
}: EventAddToCartProps) => {
  window?.dataLayer?.push({ ecommerce: null });
  window?.dataLayer?.push({
    event: "add_to_cart",
    ecommerce: {
      currencyCode: currency,
      revenue: value,
      products: items.map(item => ({
        ...item,
      })),
    },
  });

  try {
    const addedItem = items[0];
    if (!addedItem) return;

    const cartItems = buildKlaviyoCartItems(addedItem, cart);

    const addedPrice = Number.isFinite(addedItem.price) ? addedItem.price : 0;

    klaviyoTrack("Added to Cart", {
      $email,
      $value: (cart?.total ?? 0) + addedPrice * (addedItem.quantity ?? 1),
      AddedItemProductName: addedItem.item_name,
      AddedItemProductID: addedItem.item_id,
      AddedItemSKU: addedItem.item_id,
      AddedItemCategories: addedItem.item_category
        ? [addedItem.item_category]
        : [],
      AddedItemURL: addedItem.item_url || SITE_URL,
      ImageURL: addedItem.item_image,
      AddedItemPrice: addedPrice,
      AddedItemQuantity: addedItem.quantity ?? 1,
      ItemNames: cartItems.map(item => item.ProductName),
      Items: cartItems,
    });
  } catch (e) {
    console.error(e);
  }
};

export const eventProductDetail = ({
  currency,
  items,
  value,
  $email,
}: EventCartProps) => {
  window?.dataLayer?.push({ ecommerce: null });
  window?.dataLayer?.push({
    event: "view_item",
    ecommerce: {
      currency,
      value,
      items,
    },
  });

  const item = items[0];
  if (item) {
    klaviyoTrack("Viewed Product", {
      $email,
      ProductName: item.item_name,
      ProductID: item.item_id,
      Categories: item.item_category,
      ImageURL: item.item_image,
      URL: item.item_url,
      Brand: "Camilla Pihl",
      Price: item.price,
    });
  }

  window.dataLayer?.push({ ecommerce: null });
  window?.dataLayer?.push({
    event: "product_detail",
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};

export const eventViewCart = ({ currency, value, items }: EventCartProps) => {
  window?.dataLayer?.push({ ecommerce: null });
  window?.dataLayer?.push({
    event: "view_cart",
    ecommerce: {
      currencyCode: currency,
      revenue: value,
      products: items,
    },
  });
};

export const eventBeginCheckout = ({
  currency,
  value,
  items,
}: EventCartProps) => {
  window?.dataLayer?.push({ ecommerce: null });
  window?.dataLayer?.push({
    event: "begin_checkout",
    ecommerce: {
      currencyCode: currency,
      revenue: value,
      products: items,
    },
  });
};

interface EventPurchaseProps {
  transaction_id: string;
  tax: number;
  shipping: number;
  coupon: string | CentraVoucher;
  currency: string;
  value: number;
  items: GTagItem[];
  email?: string;
}

export const eventPurchase = ({
  transaction_id,
  tax,
  shipping,
  coupon,
  currency,
  value,
  items,
  email,
}: EventPurchaseProps) => {
  try {
    /* GTM */
    window?.dataLayer?.push({
      event: "purchase",
      ecommerce: {
        affiliation: "Online Store",
        transaction_id,
        tax,
        shipping,
        coupon: typeof coupon === "string" ? coupon : coupon?.voucher || "",
        currency: currency,
        value: value,
        items,
      },
    });
  } catch (error) {
    console.log(error);
  }
};

interface EventStartedCheckoutProps extends EventCartProps {
  email: string;
  checkoutUrl: string;
}

export const buildCheckoutRecoveryUrl = (items: CartItemMaybeHooked[]) => {
  const skus = items
    .map(item => item.sku)
    .filter(Boolean)
    .join(",");
  return `${SITE_URL}/checkout?skus=${encodeURIComponent(skus)}`;
};
